import {
	type EcoleOffer,
	type NouveauProfil,
	type NouvelleSelection,
	VERSANT_LIMITES,
} from "@shared/ecole.types";
import { Plus, Send, Trash2 } from "lucide-react";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ds/Button";
import { Card, CardHeader } from "@/components/ds/Card";
import { GestureError, SelectInput, TextAreaInput, TextInput } from "@/components/ds/Fields";
import { FilterChip } from "@/components/ds/FilterChip";
import { LinkButton } from "@/components/ds/LinkButton";
import { type Echec, useGeste } from "./useActivite";

/**
 * Envoyer des profils à une entreprise.
 *
 * Deux destinataires possibles, et un seul à la fois : le contact d'une offre
 * publiée (le cas courant, ouvert depuis la carte de l'offre), ou une adresse que
 * l'école saisit, ce qui invite l'entreprise dans son espace. L'école recopie ce
 * qu'elle accepte de montrer de chaque étudiant, et rien d'autre.
 */

type Destination = "offre" | "adresse";

const PROFIL_VIDE: NouveauProfil = { fullName: "", headline: "", program: "", cvUrl: "" };

/** Le serveur ne prend qu'un lien en https : un autre deviendrait un lien piégé dans l'espace. */
function lienRefuse(cvUrl: string | null | undefined): boolean {
	const lien = cvUrl?.trim() ?? "";
	return lien !== "" && !lien.startsWith("https://");
}

/** Le vide part en `null` : le serveur refuse une chaîne vide là où il attend une URL. */
function nettoyer(profil: NouveauProfil): NouveauProfil {
	const ou = (valeur: string | null | undefined) => (valeur?.trim() ? valeur.trim() : null);
	return {
		fullName: profil.fullName.trim(),
		headline: ou(profil.headline),
		program: ou(profil.program),
		cvUrl: ou(profil.cvUrl),
	};
}

export function NouvelleSelectionPage({
	offers,
	envoyer,
}: {
	offers: EcoleOffer[];
	envoyer: (selection: NouvelleSelection) => Promise<Echec>;
}) {
	const navigate = useNavigate();
	const [params] = useSearchParams();
	const publiees = offers.filter((offer) => offer.status === "validee");
	const demandee = publiees.find((offer) => offer.id === params.get("offre"));

	const [destination, setDestination] = useState<Destination>(
		publiees.length > 0 ? "offre" : "adresse",
	);
	const [offerId, setOfferId] = useState(demandee?.id ?? publiees[0]?.id ?? "");
	const [email, setEmail] = useState("");
	const [contactName, setContactName] = useState("");
	const [title, setTitle] = useState("");
	const [message, setMessage] = useState("");
	const [profils, setProfils] = useState<NouveauProfil[]>([PROFIL_VIDE]);
	const geste = useGeste();

	const changerProfil = (index: number, patch: Partial<NouveauProfil>) =>
		setProfils((courants) =>
			courants.map((profil, i) => (i === index ? { ...profil, ...patch } : profil)),
		);

	const pret =
		title.trim() !== "" &&
		profils.every((profil) => profil.fullName.trim() !== "" && !lienRefuse(profil.cvUrl)) &&
		(destination === "offre" ? offerId !== "" : email.trim() !== "");
	/*
	 * Le bouton d'envoi reste ACTIF. Grisé tant qu'un champ manquait, il ne disait
	 * pas lequel : sur trois profils, il fallait relire chaque carte. Au premier clic
	 * sur un envoi incomplet, chaque champ manquant porte son erreur.
	 */
	const [tente, setTente] = useState(false);
	const manque = (vide: boolean, message: string) => (tente && vide ? message : null);

	async function envoyerSelection() {
		if (!pret) {
			setTente(true);
			return;
		}
		const resultat = await geste.lancer(() =>
			envoyer({
				...(destination === "offre"
					? { offerId }
					: { email: email.trim(), contactName: contactName.trim() || null }),
				title: title.trim(),
				message: message.trim() || null,
				profiles: profils.map(nettoyer),
			}),
		);
		if (!resultat) navigate("/ecole/profils");
	}

	return (
		<div className="flex flex-col gap-5">
			<h1 className="t-h1 text-mg-heading">Envoyer des profils</h1>

			<Card padded={false}>
				<CardHeader title="Destinataire" />
				<div className="flex flex-col gap-4 p-6">
					{publiees.length > 0 ? (
						<div className="flex flex-wrap gap-2">
							<FilterChip
								selected={destination === "offre"}
								onClick={() => setDestination("offre")}
							>
								Une offre publiée
							</FilterChip>
							<FilterChip
								selected={destination === "adresse"}
								onClick={() => setDestination("adresse")}
							>
								Une autre entreprise
							</FilterChip>
						</div>
					) : null}

					{destination === "offre" ? (
						<SelectInput
							label="Offre"
							hint="Les profils partent au contact qui a déposé l'offre."
							options={publiees.map((offer) => ({
								value: offer.id,
								label: [offer.job, offer.companyName].filter(Boolean).join(" · "),
							}))}
							value={offerId}
							onChange={setOfferId}
						/>
					) : (
						<div className="grid gap-4 md:grid-cols-2">
							<TextInput
								label="Email du contact"
								hint="L'entreprise ouvre son espace avec cette adresse, sans compte."
								type="email"
								max={320}
								required
								error={manque(email.trim() === "", "Indiquez l'email du contact.")}
								value={email}
								onChange={setEmail}
							/>
							<TextInput
								label="Nom du contact"
								max={VERSANT_LIMITES.contactName}
								value={contactName}
								onChange={setContactName}
							/>
						</div>
					)}
				</div>
			</Card>

			<Card padded={false}>
				<CardHeader title="Votre message" />
				<div className="flex flex-col gap-4 p-6">
					<TextInput
						label="Titre de l'envoi"
						hint="L'entreprise le lit en tête de la liste."
						max={VERSANT_LIMITES.selectionTitle}
						required
						error={manque(title.trim() === "", "Donnez un titre à l'envoi.")}
						value={title}
						onChange={setTitle}
					/>
					<TextAreaInput
						label="Message"
						max={VERSANT_LIMITES.selectionMessage}
						rows={4}
						value={message}
						onChange={setMessage}
					/>
				</div>
			</Card>

			<Card padded={false}>
				<CardHeader
					title="Profils"
					action={
						<Button
							size="sm"
							icon={<Plus className="size-4" aria-hidden="true" />}
							disabled={profils.length >= VERSANT_LIMITES.profils}
							onClick={() => setProfils((courants) => [...courants, PROFIL_VIDE])}
						>
							Ajouter un profil
						</Button>
					}
				/>
				<ol className="divide-y divide-mg-border-subtle">
					{profils.map((profil, index) => (
						// L'ordre est celui de l'école, et une ligne ne se déplace pas.
						// biome-ignore lint/suspicious/noArrayIndexKey: les lignes n'ont pas d'identité avant l'envoi.
						<li key={index} className="grid gap-4 px-6 py-5 md:grid-cols-2">
							<TextInput
								label="Nom de l'étudiant"
								max={VERSANT_LIMITES.profilNom}
								required
								error={manque(profil.fullName.trim() === "", "Indiquez le nom de l'étudiant.")}
								value={profil.fullName}
								onChange={(fullName) => changerProfil(index, { fullName })}
							/>
							<TextInput
								label="Formation"
								max={VERSANT_LIMITES.profilChamp}
								value={profil.program ?? ""}
								onChange={(program) => changerProfil(index, { program })}
							/>
							<TextInput
								label="En une ligne"
								max={VERSANT_LIMITES.profilChamp}
								value={profil.headline ?? ""}
								onChange={(headline) => changerProfil(index, { headline })}
							/>
							<TextInput
								label="Lien vers le CV"
								hint="Une adresse en https."
								error={lienRefuse(profil.cvUrl) ? "Le lien doit commencer par https://." : null}
								type="url"
								max={VERSANT_LIMITES.cvUrl}
								value={profil.cvUrl ?? ""}
								onChange={(cvUrl) => changerProfil(index, { cvUrl })}
							/>
							{profils.length > 1 ? (
								<div className="md:col-span-2">
									<Button
										size="sm"
										icon={<Trash2 className="size-4" aria-hidden="true" />}
										onClick={() => setProfils((courants) => courants.filter((_, i) => i !== index))}
									>
										Retirer ce profil
									</Button>
								</div>
							) : null}
						</li>
					))}
				</ol>
			</Card>

			<div className="flex flex-col gap-3">
				<GestureError
					message={
						tente && !pret
							? "Il manque des informations : elles sont indiquées sous chaque champ."
							: geste.echec
					}
				/>
				<div className="flex flex-wrap gap-2">
					<Button
						tone="primary"
						icon={<Send className="size-4" aria-hidden="true" />}
						disabled={geste.enCours}
						onClick={envoyerSelection}
					>
						{profils.length === 1 ? "Envoyer le profil" : `Envoyer les ${profils.length} profils`}
					</Button>
					<LinkButton to="/ecole/profils" size="md">
						Annuler
					</LinkButton>
				</div>
			</div>
		</div>
	);
}
